import { usersApiPaths } from '@/hooks/api/_paths'
import type { ConsentSettings, ConsentSettingsPolicyData } from '@/hooks/api/_types'
import { consentSettingsKey } from '@/hooks/api/use-consent-settings-query'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query'

export function useConsentSettingsUpdateMutation({
  onMutate,
  onSettled,
  onError,
  ...options
}: UseMutationOptions<ConsentSettings, Error, ConsentSettings> = {}) {
  const queryClient = useQueryClient()
  const { apiRequest } = useApiRequest()
  return useMutation({
    mutationKey: ['update-consent'],
    mutationFn: async (data: ConsentSettings) => {
      const response = await apiRequest({
        path: usersApiPaths.privacyPreferences,
        options: {
          method: 'POST',
          body: JSON.stringify(port(data)),
        },
      })

      if (response.status !== 200) {
        throw new Error('FAILED_TO_UPDATE_CONSENT_SETTINGS')
      }
      return data
    },
    onMutate: async (vars, context) => {
      await queryClient.cancelQueries({ queryKey: consentSettingsKey })

      const previousData = queryClient.getQueryData(consentSettingsKey)

      queryClient.setQueryData(consentSettingsKey, vars)

      if (onMutate) await onMutate(vars, context)

      return { previousData }
    },

    onError: (err, vars, result, context) => {
      if (result?.previousData) {
        queryClient.setQueryData(consentSettingsKey, result.previousData)
      }
      return onError?.(err, vars, result, context)
    },
    onSettled: async (data, error, vars, result, context) => {
      await queryClient.invalidateQueries({ queryKey: consentSettingsKey })
      return onSettled?.(data, error, vars, result, context)
    },
    ...options,
  })
}

const port = ({ allowData, allowMarketing }: ConsentSettings): ConsentSettingsPolicyData => ({
  policies: [
    { name: 'ALLOW_DATA', preference: allowData },
    { name: 'ALLOW_MARKETING_COMMS', preference: allowMarketing },
  ],
})
